import { REQUIRED_SOURCE_PATHS } from "../templates/webhookTemplates";
import { TWebhookMappingRow } from "../types";

export const makeId = (prefix: string): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

export const parsePathTokens = (path: string): (string | number)[] => {
  const tokens: (string | number)[] = [];
  const regex = /[^.[\]]+|\[(\d+)\]/g;
  let match: RegExpExecArray | null;

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(path))) {
    if (match[1] !== undefined) {
      tokens.push(parseInt(match[1], 10));
    } else {
      tokens.push(match[0]);
    }
  }

  return tokens;
};

export const getValueAtPath = (
  data: unknown,
  path: string,
): unknown | undefined => {
  if (!path) return undefined;
  return parsePathTokens(path).reduce((acc: any, token) => {
    if (acc == null) return undefined;
    return acc[token as keyof typeof acc];
  }, data as any);
};

export const setValueAtPath = (
  target: Record<string, unknown>,
  path: string,
  value: unknown,
): void => {
  const tokens = parsePathTokens(path);
  if (!tokens.length) return;

  let current: any = target;

  tokens.forEach((token, index) => {
    const isLast = index === tokens.length - 1;
    if (isLast) {
      current[token as keyof typeof current] = value;
      return;
    }

    const next = tokens[index + 1];
    if (current[token as keyof typeof current] == null) {
      current[token as keyof typeof current] =
        typeof next === "number" ? [] : {};
    }

    current = current[token as keyof typeof current];
  });
};

export const buildDefaultMappingRows = (): TWebhookMappingRow[] =>
  REQUIRED_SOURCE_PATHS.map((sourcePath) => ({
    id: makeId("mapping"),
    target_field: "",
    source_path: sourcePath,
    default_value: "",
    literal_value: "",
    include_if_none: false,
  }));

export const ensureRequiredMappings = (
  rows: TWebhookMappingRow[],
): TWebhookMappingRow[] => {
  const existingPaths = new Set(
    rows.map((row) => row.source_path.trim()).filter(Boolean),
  );

  const missingRows = REQUIRED_SOURCE_PATHS.filter(
    (path) => !existingPaths.has(path),
  ).map((sourcePath) => ({
    id: makeId("mapping"),
    target_field: "",
    source_path: sourcePath,
    default_value: "",
    literal_value: "",
    include_if_none: false,
  }));

  return [...rows, ...missingRows];
};

export const buildPreviewPayload = (
  mappings: TWebhookMappingRow[],
  sampleSubmission: unknown,
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};

  mappings.forEach((row) => {
    const targetField = row.target_field.trim();
    if (!targetField) return;

    const sourcePath = row.source_path.trim();
    const literalValue = row.literal_value.trim();
    const defaultValue = row.default_value.trim();

    const hasLiteralValue = literalValue.length > 0;
    let resolvedValue: unknown = undefined;

    if (hasLiteralValue) {
      resolvedValue = literalValue;
    } else if (sourcePath) {
      resolvedValue = getValueAtPath(sampleSubmission, sourcePath);
    }

    if (
      (resolvedValue === undefined ||
        resolvedValue === null ||
        resolvedValue === "") &&
      !hasLiteralValue &&
      defaultValue.length > 0
    ) {
      resolvedValue = defaultValue;
    }

    const shouldInclude =
      resolvedValue !== undefined &&
      resolvedValue !== null &&
      resolvedValue !== ""
        ? true
        : row.include_if_none;

    if (!shouldInclude) return;

    setValueAtPath(payload, targetField, resolvedValue ?? null);
  });

  return payload;
};

export const extractSourcePaths = (
  sampleObject: unknown,
  maxDepth = 4,
): string[] => {
  const paths: string[] = [];

  const visit = (value: unknown, prefix: string, depth: number) => {
    if (depth > maxDepth || value == null) return;

    if (Array.isArray(value)) {
      const length = Math.min(value.length, 4);
      for (let i = 0; i < length; i += 1) {
        const arrayPath = `${prefix}[${i}]`;
        paths.push(arrayPath);
        visit(value[i], arrayPath, depth + 1);
      }
      return;
    }

    if (typeof value === "object") {
      Object.entries(value as Record<string, unknown>).forEach(
        ([key, child]) => {
          const nextPath = prefix ? `${prefix}.${key}` : key;
          paths.push(nextPath);
          visit(child, nextPath, depth + 1);
        },
      );
    }
  };

  visit(sampleObject, "", 0);

  return Array.from(new Set(paths)).sort();
};

export const toJsonPretty = (value: unknown): string =>
  JSON.stringify(value, null, 2);
